import React, { useState } from "react";
import {
  SUBMISSION_MISSING_VALUE,
  buildSubmissionFacts,
  getSubmissionCodeClassName,
  getSubmissionText,
  getSubmissionTitle,
  submissionTypeBadgeClasses,
} from "../../../utils/submissionFormatters";

const badgeBaseClass =
  "inline-flex max-w-full items-center rounded-md px-2.5 py-1 text-[11px] font-semibold tracking-[0.02em]";

const DrugSubmissionItem = ({ submission, prominent = false }) => {
  if (!submission) {
    return null;
  }

  const badge = submission.badge || null;
  const badgeLabel = getSubmissionText(badge?.label);
  const submissionType = getSubmissionText(submission.submission_type);
  const typeClassName =
    submissionTypeBadgeClasses[submissionType] || submissionTypeBadgeClasses.N_A;

  return (
    <div
      className={`rounded-xl border px-4 py-3 ${
        prominent
          ? "border-emerald-300/30 bg-emerald-500/10"
          : "border-slate-700/80 bg-slate-950/40"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-50">
            {prominent ? "Original Approval" : getSubmissionTitle(submission)}
          </p>
          {prominent ? (
            <p className="mt-1 text-xs font-medium text-emerald-100">
              {getSubmissionTitle(submission, "ORIG")}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <span className={`${badgeBaseClass} ${typeClassName}`}>{submissionType}</span>
          <span className={`${badgeBaseClass} ${getSubmissionCodeClassName(badge)}`}>
            {badgeLabel}
          </span>
        </div>
      </div>

      <dl className="mt-3 grid gap-2 text-left text-xs text-slate-300 sm:grid-cols-2">
        {buildSubmissionFacts(submission).map(([label, value]) => (
          <div key={`${label}-${value}`}>
            <dt className="font-medium uppercase tracking-[0.12em] text-slate-500">{label}</dt>
            <dd className="mt-1 text-slate-100">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const DrugSubmissionsFieldValue = ({ value }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const originalSubmission = value?.originalSubmission || null;
  const supplementalSubmissions = Array.isArray(value?.supplementalSubmissions)
    ? value.supplementalSubmissions
    : [];

  return (
    <div className="flex w-full flex-col gap-3 md:max-w-[70%]">
      {originalSubmission ? (
        <DrugSubmissionItem submission={originalSubmission} prominent />
      ) : (
        <div className="rounded-xl border border-dashed border-slate-700 px-4 py-3 text-sm text-slate-300">
          Original Approval: {SUBMISSION_MISSING_VALUE}
        </div>
      )}

      {supplementalSubmissions.length ? (
        <div className="rounded-xl border border-slate-800 bg-slate-950/30">
          <button
            type="button"
            onClick={() => setIsExpanded((currentValue) => !currentValue)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-slate-100 transition-colors hover:text-white"
            aria-expanded={isExpanded}
          >
            <span>Supplemental Submissions ({supplementalSubmissions.length})</span>
            <span className="text-xs uppercase tracking-[0.16em] text-slate-400">
              {isExpanded ? "Hide" : "Show"}
            </span>
          </button>

          {isExpanded ? (
            <div className="space-y-3 border-t border-slate-800 px-3 py-3">
              {supplementalSubmissions.map((submission, index) => (
                <DrugSubmissionItem
                  key={`${submission.submission_type || "SUPPL"}-${submission.submission_number || index}`}
                  submission={submission}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-700 px-4 py-3 text-sm text-slate-300">
          Supplemental Submissions: {SUBMISSION_MISSING_VALUE}
        </div>
      )}
    </div>
  );
};

export { DrugSubmissionItem };
export default DrugSubmissionsFieldValue;
